import { isPlatformBrowser } from '@angular/common'
import { effect, inject, Injectable, PLATFORM_ID, REQUEST, signal } from '@angular/core'
import { Customer, RefreshCustomerResponseDto } from '@shared/interfaces'
import { firstValueFrom } from 'rxjs'
import { HttpClient, HttpContext } from '@angular/common/http'
import { BankAuthScope } from '@shared/types'
import { CookieService } from 'ngx-cookie-service'
import { SkipErrorLog } from './error-log.interceptor'

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient)
  private cookieService = inject(CookieService)
  private platformId = inject(PLATFORM_ID)
  private request = inject(REQUEST, { optional: true })
  private readonly scopeCookieKey = 'scope'
  private loadingCustomerPromise: Promise<Customer | null> | null = null

  #customer = signal<Customer | null>(null)
  customer = this.#customer.asReadonly()
  #scope = signal<BankAuthScope | null>(null)
  scope = this.#scope.asReadonly()

  constructor() {
    this.#scope.set(this.readJsonCookie<BankAuthScope>(this.scopeCookieKey))

    effect(() => {
      const customer = this.customer()
      if (customer) {
        // Clarity
        if (isPlatformBrowser(this.platformId)) {
          const clarity = (globalThis as any).clarity
          if (typeof clarity === 'function') {
            clarity('identify', String(customer.id))
          }
        }
      }
    })
  }

  setCustomer(customer: Customer | null) {
    this.#customer.set(customer)
  }

  setScope(scope: BankAuthScope | null) {
    this.#scope.set(scope)
    if (!scope) {
      this.deleteCookie(this.scopeCookieKey)
    }
  }

  async logout() {
    if (this.customer()) {
      await firstValueFrom(this.http.post('/api/public/v1/users/logout', {})).catch(() => null)
      // Auth token lives in HttpOnly cookie and must be cleared by the server.
      await firstValueFrom(this.http.post('/auth/logout', {})).catch(() => null)
    }
    this.setCustomer(null)
    this.setScope(null)
  }

  async ensureCustomerLoaded(): Promise<Customer | null> {
    const customer = this.customer()
    if (customer) {
      return customer
    }

    if (!this.loadingCustomerPromise) {
      this.loadingCustomerPromise = firstValueFrom(
        this.http.get<RefreshCustomerResponseDto>('/api/v1/users/self', {
          context: new HttpContext().set(SkipErrorLog, true),
        }),
      )
        .then(({ user }) => {
          this.setCustomer(user)
          return user
        })
        .catch(() => null)
        .finally(() => {
          this.loadingCustomerPromise = null
        })
    }

    return this.loadingCustomerPromise
  }

  private readJsonCookie<T>(name: string): T | null {
    const value = this.readCookie(name)
    if (!value) {
      return null
    }

    try {
      return JSON.parse(value) as T
    } catch {
      return null
    }
  }

  private readCookie(name: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      const value = this.cookieService.get(name)
      return value || null
    }

    const header = this.request?.headers?.get('cookie')
    if (!header) {
      return null
    }

    const chunk = header
      .split(';')
      .map(item => item.trim())
      .find(item => item.startsWith(`${name}=`))
    if (!chunk) {
      return null
    }

    return decodeURIComponent(chunk.slice(name.length + 1))
  }

  private deleteCookie(name: string) {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    this.cookieService.delete(name, '/')
  }
}
